// Shared helpers and the smaller page effects of the marketing site, ported from
// the approved prototype (app.js v13). Every effect bails out when its markup is
// not on the page, so the same bundle serves every marketing page.
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import type Lenis from 'lenis'
import type { Constellation } from './constellation'

export const G = gsap
export const ST = ScrollTrigger

/**
 * Live handles shared by the modules. `gl` stays null until three.js has loaded
 * (or for good, without WebGL), so every caller reads it at call time.
 */
export const motion: { gl: Constellation | null; lenis: Lenis | null } = { gl: null, lenis: null }

export const $ = <T extends Element = HTMLElement>(s: string, r: ParentNode = document): T | null => r.querySelector<T>(s)
export const $$ = <T extends Element = HTMLElement>(s: string, r: ParentNode = document): T[] => [...r.querySelectorAll<T>(s)]
export const fmt = (n: number): string => Math.round(n).toLocaleString('es-AR')
export const isMobile = (): boolean => matchMedia('(max-width: 900px)').matches
export const fine = (): boolean => matchMedia('(pointer: fine)').matches
export const prefersReducedMotion = (): boolean => matchMedia('(prefers-reduced-motion: reduce)').matches
export const grid = (): number => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--grid')) || 1480
export const rand = (a: number, b: number): number => a + Math.random() * (b - a)
/** Fisher–Yates, in place. */
export const shuffle = <T>(xs: T[]): T[] => {
  for (let i = xs.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [xs[i], xs[j]] = [xs[j], xs[i]] }
  return xs
}

/** Wraps every word of `el` in a clipping span so headings can rise word by word. */
export const splitWords = (el: HTMLElement, cls = 'wi'): HTMLElement[] => {
  const words = (el.textContent || '').trim().split(/\s+/)
  el.replaceChildren()
  words.forEach((w, i) => {
    if (i) el.append(' ')
    const outer = document.createElement('span'); outer.className = 'w'
    const inner = document.createElement('span'); inner.className = cls; inner.textContent = w
    outer.append(inner); el.append(outer)
  })
  return $$(`.${cls}`, el)
}

export interface OdoColumn { s: HTMLElement; d: number; c: HTMLElement }

/** Odometer: every digit becomes a strip of 0-9 twice, rolled into place by `odoRoll`. */
export const odoBuild = (el: HTMLElement, text: string): OdoColumn[] => {
  el.innerHTML = ''
  const sr = document.createElement('span'); sr.className = 'sr-only'; sr.textContent = text
  const v = document.createElement('span'); v.className = 'odo'; v.setAttribute('aria-hidden', 'true')
  const cols: OdoColumn[] = []
  for (const ch of text) {
    if (/\d/.test(ch)) {
      const c = document.createElement('span'); c.className = 'od'
      const s = document.createElement('span'); s.className = 'ods'
      for (let k = 0; k < 20; k++) { const d = document.createElement('span'); d.textContent = String(k % 10); s.appendChild(d) }
      c.appendChild(s); v.appendChild(c); cols.push({ s, d: Number(ch), c })
    } else { const t = document.createElement('span'); t.textContent = ch; v.appendChild(t) }
  }
  el.append(sr, v)
  fitOdo(cols)
  // The first measure may use the fallback font; measure again once the web font is in.
  if (document.fonts) void document.fonts.ready.then(() => fitOdo(cols))
  return cols
}

/**
 * Each column takes the width of the digit it lands on. A strip of 0-9 is as wide as its widest
 * digit, which left a gap around a narrow 1 (the «+10» stat).
 */
const fitOdo = (cols: OdoColumn[]): void => cols.forEach((col) => {
  const m = document.createElement('span')
  m.textContent = String(col.d)
  m.style.cssText = 'position:absolute;visibility:hidden;white-space:pre'
  col.c.parentElement!.appendChild(m)
  // In em, so the column follows the viewport-relative font size on resize or rotation.
  col.c.style.width = `${m.getBoundingClientRect().width / parseFloat(getComputedStyle(col.c).fontSize)}em`
  m.remove()
})

export const odoRoll = (cols: OdoColumn[], dur = 1.8): void => {
  // Measure again on roll: the web font loads without blocking, so the build-time measure (and
  // even `document.fonts.ready`) can predate it.
  fitOdo(cols)
  cols.forEach((c, i) => {
    G.fromTo(c.s, { yPercent: 0 }, { yPercent: -((10 + c.d) / 20) * 100, duration: dur + (cols.length - i) * 0.14, ease: 'power4.out' })
  })
}

/* ------------------------------------------------------------------ */

/** Grain texture, generated once. */
export function wireGrain(): void {
  const el = $('.grain')
  if (!el) return
  const c = document.createElement('canvas')
  c.width = c.height = 160
  const g = c.getContext('2d')
  if (!g) return
  const d = g.createImageData(160, 160)
  for (let i = 0; i < d.data.length; i += 4) { const v = Math.random() * 255; d.data[i] = d.data[i + 1] = d.data[i + 2] = v; d.data[i + 3] = 255 }
  g.putImageData(d, 0, 0)
  el.style.backgroundImage = `url(${c.toDataURL()})`
}

/** The nav turns solid past the top and hides while scrolling down. */
export function wireNav(lenis: Lenis | null): void {
  const nav = $('.nav')
  if (!nav) return
  let lastY = 0
  let idle = 0
  const onScroll = (y: number) => {
    nav.classList.toggle('solid', y > 30)
    if (y > lastY + 2 && y > 400) nav.classList.add('hide')
    if (y < lastY - 2) nav.classList.remove('hide')
    lastY = y
    // It hides while the visitor scrolls down, and comes back as soon as they stop:
    // «Ingresar» and «Agendá una demo» must never stay out of reach.
    clearTimeout(idle)
    idle = window.setTimeout(() => nav.classList.remove('hide'), 450)
  }
  if (lenis) lenis.on('scroll', (e: Lenis) => onScroll(e.scroll))
  else addEventListener('scroll', () => onScroll(scrollY), { passive: true })
  onScroll(scrollY)
}

/** Cursor spotlight, ring and magnetic buttons (fine pointers only). */
export function wireCursor(): void {
  if (!fine()) return
  const sp = $('.spot'); const ring = $('.ring')
  if (sp && ring) {
    let x = innerWidth / 2; let y = innerHeight / 2; let tx = x; let ty = y; let rx = x; let ry = y
    addEventListener('pointermove', (e) => { tx = e.clientX; ty = e.clientY; sp.style.opacity = '1'; ring.classList.add('on') })
    document.addEventListener('pointerleave', () => ring.classList.remove('on'))
    document.addEventListener('pointerover', (e) => ring.classList.toggle('hot', !!(e.target as Element).closest?.('a, button')))
    const loop = () => {
      x += (tx - x) * 0.12; y += (ty - y) * 0.12; rx += (tx - rx) * 0.24; ry += (ty - ry) * 0.24
      sp.style.transform = `translate3d(${x}px,${y}px,0)`
      ring.style.transform = `translate3d(${rx}px,${ry}px,0)`
      requestAnimationFrame(loop)
    }
    loop()
  }
  $$('.mag').forEach((b) => {
    b.addEventListener('pointermove', (e) => {
      const r = b.getBoundingClientRect()
      b.style.translate = `${((e.clientX - r.left) / r.width - 0.5) * 14}px ${((e.clientY - r.top) / r.height - 0.5) * 10}px`
    })
    b.addEventListener('pointerleave', () => { b.style.translate = '' })
  })
}

/** Reading progress bar across the top. */
export function wireProgress(): void {
  if (!$('.progress')) return
  G.to('.progress', { scaleX: 1, ease: 'none', scrollTrigger: { trigger: document.body, start: 'top top', end: 'bottom bottom', scrub: true } })
}

/** Kinetic band: two rows drift in opposite directions and surge with scroll speed. */
export function wireBand(): void {
  const rows = $$('.band-row')
  if (!rows.length) return
  rows.forEach((r) => { const c = $('.band-in', r)!.cloneNode(true) as HTMLElement; c.setAttribute('aria-hidden', 'true'); r.appendChild(c) })
  const st = rows.map((r, i) => ({ r, x: 0, w: 0, dir: i % 2 ? 1 : -1 }))
  const measure = () => st.forEach((s) => { s.w = $('.band-in', s.r)!.offsetWidth; if (s.dir > 0) s.x = -s.w / 2 })
  measure()
  addEventListener('resize', measure)
  let vel = 0; let skew = 0
  ST.create({ trigger: '.band', start: 'top bottom', end: 'bottom top', onUpdate: (self) => { vel = self.getVelocity() } })
  G.ticker.add((_time, dt) => {
    const boost = Math.min(12, Math.abs(vel) / 220)
    skew += (Math.max(-9, Math.min(9, vel / 280)) - skew) * 0.1
    vel *= 0.92
    st.forEach((s) => {
      if (!s.w) return
      s.x += s.dir * (0.7 + boost) * (dt / 16.7)
      if (s.x <= -s.w) s.x += s.w
      if (s.x >= 0) s.x -= s.w
      s.r.style.transform = `translate3d(${s.x}px,0,0) skewX(${-skew}deg)`
    })
  })
}

/** Generic heading reveals and copy fade-ins. */
export function wireHeadings(): void {
  $$('main .h2').forEach((h) => {
    const ws = splitWords(h, 'wi')
    G.from(ws, {
      yPercent: 115, rotate: 5, duration: 1, ease: 'power4.out', stagger: 0.045, scrollTrigger: { trigger: h, start: 'top 86%' },
      onComplete: () => h.classList.add('shine'),
    })
  })
  G.utils.toArray<HTMLElement>('.ch-copy .lead, .ch-copy .chip, .coti-head .chip, .copi-head .lead, .copi-head .chip, .stack-s .chip, .hz-head .chip').forEach((el) => {
    G.from(el, { y: 24, opacity: 0, duration: 0.9, ease: 'power3.out', scrollTrigger: { trigger: el, start: 'top 88%' } })
  })
}

/** Manifesto: words light up, then a strike runs across them word by word. */
export function wireManifesto(): void {
  const lines = $$('.mani .ml')
  const end = $('.mani-end')
  if (!lines.length || !end) return
  lines.forEach((ml) => {
    const ws = (ml.textContent || '').trim().split(/\s+/)
    ml.innerHTML = ws.map((w, k) => `<span class="mw${k < ws.length - 1 ? ' gap' : ''}">${w}</span>`).join(' ')
  })
  G.set($$('.mani .mw'), { opacity: 0.13, '--s': 0 })
  G.set(end, { opacity: 0, scale: 0.86, filter: 'blur(16px)', transformOrigin: '0% 50%' })
  const tl = G.timeline({ scrollTrigger: { trigger: '.mani', start: 'top 72%', end: 'bottom 80%', scrub: 0.6 } })
  lines.forEach((ml) => {
    const ws = $$('.mw', ml)
    tl.to(ws, { opacity: 1, stagger: 0.12, duration: 0.3, ease: 'none' })
      .to(ws, { '--s': 1, stagger: 0.07, duration: 0.18, ease: 'power1.inOut' }, '+=0.05')
      .to(ws, { opacity: 0.32, duration: 0.3 }, '-=0.1')
  })
  tl.to(end, { opacity: 1, scale: 1, filter: 'blur(0px)', duration: 1.2, ease: 'power3.out' })
}

/** Bento: entrance, tilt, spotlight and the little live demos. */
export function wireBento(): void {
  const tiles = $$('.bt')
  if (!tiles.length) return
  G.set(tiles, { y: 50, opacity: 0 })
  ST.batch(tiles, { start: 'top 90%', once: true, onEnter: (b) => G.to(b, { y: 0, opacity: 1, duration: 1, ease: 'power3.out', stagger: 0.08 }) })
  const io = new IntersectionObserver((es) => es.forEach((e) => e.target.classList.toggle('run', e.isIntersecting)), { threshold: 0.25 })
  tiles.forEach((t) => {
    io.observe(t)
    if (!fine()) return
    t.addEventListener('pointermove', (e) => {
      const r = t.getBoundingClientRect(); const x = (e.clientX - r.left) / r.width; const y = (e.clientY - r.top) / r.height
      t.style.setProperty('--mx', `${x * 100}%`); t.style.setProperty('--my', `${y * 100}%`)
      t.style.setProperty('--rx', `${(0.5 - y) * 5}deg`); t.style.setProperty('--ry', `${(x - 0.5) * 7}deg`)
    })
    t.addEventListener('pointerleave', () => { t.style.setProperty('--rx', '0deg'); t.style.setProperty('--ry', '0deg') })
  })
  const sq = $('.sq'); const rs = $$('.srch .r'); const word = 'AF 482'
  const tile = sq?.closest('.bt')
  if (!sq || !tile) return
  let i = 0; let dirn = 1
  setInterval(() => {
    if (!tile.classList.contains('run')) return
    i += dirn
    if (i > word.length + 6) dirn = -1
    if (i <= 0) { i = 0; dirn = 1 }
    sq.textContent = word.slice(0, Math.min(i, word.length))
    rs.forEach((r, k) => r.classList.toggle('on', i >= word.length + k))
  }, 170)
}

/** Proof: numbers roll like an odometer when they arrive; the quote rises. */
export function wireProof(): void {
  $$('.nums [data-odo]').forEach((el) => {
    const cols = odoBuild(el, el.dataset.odo || '')
    ST.create({ trigger: el, start: 'top 88%', once: true, onEnter: () => odoRoll(cols, 2) })
  })
  if ($('.quote p')) G.from('.quote p', { y: 40, opacity: 0, duration: 1.2, ease: 'power3.out', scrollTrigger: { trigger: '.quote', start: 'top 85%' } })
}

/**
 * FAQ accordion. The server renders every answer open (the no-JavaScript truth);
 * here all but the first collapse, and each button toggles `.open` + `aria-expanded`.
 */
export function wireFaq(refresh: boolean): void {
  $$('.qa').forEach((qa, i) => {
    const b = $('button', qa)
    if (!b) return
    const set = (open: boolean) => { qa.classList.toggle('open', open); b.setAttribute('aria-expanded', String(open)) }
    set(i === 0)
    b.addEventListener('click', () => {
      set(!qa.classList.contains('open'))
      if (refresh) setTimeout(() => ST.refresh(), 600)
    })
  })
}

/** Final: magnetic letters and the card wall. */
export function wireFinal(): void {
  const h = $('[data-mag]')
  if (!h) return
  const chars: { el: HTMLElement; y: (v: number) => void; s: (v: number) => void }[] = []
  const phrase = h.textContent || ''
  h.replaceChildren()
  phrase.split(' ').forEach((w, i) => {
    if (i) h.append(' ')
    const word = document.createElement('span'); word.style.cssText = 'display:inline-block;white-space:nowrap'
    for (const c of w) { const ch = document.createElement('span'); ch.className = 'ch1'; ch.textContent = c; word.append(ch) }
    h.append(word)
  })
  // Screen readers still get the line as one phrase.
  h.setAttribute('aria-label', phrase)
  $$('.ch1', h).forEach((c) => chars.push({ el: c, y: G.quickTo(c, 'y', { duration: 0.5, ease: 'power3.out' }), s: G.quickTo(c, 'scale', { duration: 0.5, ease: 'power3.out' }) }))
  G.from($$('.ch1', h), { yPercent: 110, opacity: 0, duration: 1, ease: 'power4.out', stagger: 0.025, scrollTrigger: { trigger: h, start: 'top 82%' } })
  ST.create({ trigger: h, start: 'top 70%', once: true, onEnter: () => { G.delayedCall(0.9, () => h.classList.add('shine')) } })
  // A tilted wall of live product cards drifts behind the closing line.
  const CARDS: [string, string, string, string][] = [
    ['Póliza 0482-117.953', 'Automotores, Martina Ruiz', 'Vigente', 'g'], ['Mejor precio', 'Toyota Corolla XEI 2022', '$ 39.540', 'c'],
    ['Cuota 6 de 12', 'Hogar, Lucía Gómez', 'Pagada', 'g'], ['Comisión conciliada', 'Liquidación de octubre', '$ 7.509', 'v'],
    ['Renovación', 'Vida, Carlos Díaz', 'vie 7/11', 'a'], ['Asesor IA', '23 pólizas vencen esta semana', 'IA', 'v'],
    ['Campaña enviada', 'Renovaciones de noviembre', '1.248', 'c'], ['Siniestro', 'Denuncia cargada desde el PDF', 'En curso', 'a'],
    ['Póliza emitida', 'Integral de comercio', 'Emitida', 'g'], ['Cotización', 'AE 913 PL, 32 compañías', '$ 52.300', 'c'],
  ]
  const wall = $('.wall-in')
  if (wall) {
    for (let r = 0; r < 6; r++) {
      const row = document.createElement('div'); row.className = 'wrow'
      const cards = CARDS.map((_, k) => CARDS[(k + r * 3) % CARDS.length])
      row.innerHTML = [...cards, ...cards].map((c) => `<div class="wc"><b>${c[0]}</b>${c[1]}<br><i class="${c[3]}">${c[2]}</i></div>`).join('')
      wall.appendChild(row)
    }
    G.fromTo('.wall', { rotateZ: 6, scale: 1.25, opacity: 0 }, { rotateZ: 0, scale: 1, opacity: 0.55, ease: 'none', scrollTrigger: { trigger: '.final', start: 'top bottom', end: 'center center', scrub: true } })
  }
  const sec = $('.final')
  if (!fine() || !sec) return
  sec.addEventListener('pointermove', (e) => {
    chars.forEach((c) => {
      const r = c.el.getBoundingClientRect(); const dx = r.left + r.width / 2 - e.clientX; const dy = r.top + r.height / 2 - e.clientY
      const f = Math.max(0, 1 - Math.hypot(dx, dy) / 260)
      c.y(-f * 28); c.s(1 + f * 0.14)
    })
  })
  sec.addEventListener('pointerleave', () => chars.forEach((c) => { c.y(0); c.s(1) }))
}
